import React, { Fragment, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { Divider } from "antd";
import _ from "lodash";
import { findIdSelector } from "../reduxSelector";
import CodeEditor from "../../../../../../helpers/CodeEditor2";
import { Modal } from "../../../../../../helpers/commonUi";
import { updateFormProperties } from "../../../../../../stateManager/store/formCreation/action";
import { updateAssetProperties } from "../../../../../../stateManager/store/assets/action";
import AddValidation from "./AddValidation";
import { ListValidationValue } from "./AddValidation/ListValidationValue";
import { assetSelector } from '../../../../../../helpers/commonFunction'
import style from "./props.module.sass";

declare const window: any;

const validationPropertyList = [
  "min",
  "max",
  "minlength",
  "maxlength",
  "pattern",
];

interface ValidationPanelProps {
  json: any;
  canEdit: boolean;
}

export default function ValidationPanel({ json, canEdit }: ValidationPanelProps) {
  const id = json.id;
  const dispatch = useDispatch();
  const params: any = useParams();

  let {
    json: { pluginName, type, widgetName },
  } = json;
  type = type === "Image" ? "ImageViewer" : type

  const [originalConfig, setOriginalConfig] = useState<any>({});
  const [pluginConfig, setPluginConfig] = useState<any>({});
  const [visible, setVisible] = useState(false);

  const selectedAssetId = useSelector((state: any) => state.assets.selectedAssetId);
  const assets = useSelector((state: any) => state.assets.list);
  const assetEditMode = useSelector((state: any) => state.assets.assetEditMode);

  let currentState: any = useSelector((state: any) => {
    return id ? findIdSelector(state, params.formId, id.split("#"), true) : [];
  });
  currentState = assetEditMode ? assetSelector(assets[selectedAssetId], id.split("#")) : currentState;

  const onChange = (data: any, key: string) => {
    const setData = _.setWith({}, key, data, Object);
    if (assetEditMode && selectedAssetId) {
      dispatch(
        updateAssetProperties({
          assetId: selectedAssetId,
          id,
          data: setData,
        })
      );
    } else {
      dispatch(
        updateFormProperties({
          formid: params.formId,
          id: json.id,
          data: setData,
        })
      );
    }
  };

  const onEditorSave = (
    editorValue: { source: string; comSource: string },
    modalId: boolean
  ) => {
    if (!canEdit) return;
    onChange(editorValue.source, "validate.fun");
    setPluginConfig((prevState: any) => ({
      ...prevState,
      fun: editorValue.source,
    }));
    if (modalId) {
      setVisible(false);
    }
  };

  useEffect(() => {
    let identifier = pluginName || type;
    identifier = identifier === "Image" ? "ImageViewer" : identifier;

    if (window[identifier]) {
      const { type: pluginType, data: plugin } = window[identifier];
      let receivedConfig: any;

      if (pluginType === "Package" && widgetName) {
        receivedConfig = plugin.widgets[widgetName].config;
      } else {
        receivedConfig = plugin.config;
      }
      const renderConfig = { ...receivedConfig };
      const validate = (currentState && currentState.validate) || {};

      // only keep the fields that belong to the validation section
      Object.keys(renderConfig).forEach((key) => {
        const field = renderConfig[key];
        if (field.section !== "validation") {
          delete renderConfig[key];
        }
      });
      setOriginalConfig(renderConfig);

      const updatedPluginConfig = Object.keys(renderConfig).reduce(
        (a: any, c: any) => {
          a[c] =
            validate[c] || renderConfig[c].value || renderConfig[c].default;
          return a;
        },
        {}
      );

      setPluginConfig(updatedPluginConfig);
    }
  }, [pluginName, type, currentState, widgetName]);

  return (
    <Fragment>
      {currentState && currentState.type && originalConfig &&
        Object.keys(originalConfig).map((configItem, index) => {
          const field = originalConfig[configItem];

          return (
            <div key={index}>
              {field.type === Function && (
                <div>
                  <p className={style.validationTitle}>{field.label}</p>
                  <div
                    data-test-id="validation_code_editor"
                    className={style.codeContainer}
                    onClick={() => setVisible(true)}
                  >
                    {pluginConfig[configItem].substr(0, 300)}
                  </div>
                  <Divider />
                </div>
              )}
            </div>
          );
        })}

      {currentState && currentState.type === "Input" && (
        <Fragment>
          <AddValidation
            data-test-id="add_validation"
            canEdit={canEdit}
            json={currentState}
            options={currentState.properties}
            updateOptions={onChange}
          />
          <Divider />
        </Fragment>
      )}
      <div style={{ display: "flex", flexDirection: "column" }}>
        {currentState &&
          currentState.properties &&
          Object.keys(currentState.properties)
            .filter((property) => validationPropertyList.includes(property))
            .map((option, index) => (
              <ListValidationValue
                data-test-id={`list_validation_value${currentState.properties}`}
                canEdit={canEdit}
                key={index}
                option={option}
                options={currentState.properties}
                updateOptions={onChange}
              />
            ))}
      </div>

      <Modal
        id="validationCode"
        dataTestId="validation_code"
        unmountOnCancel={true}
        visible={visible}
        title="CODE EDITOR"
        onCancel={() => {
          setVisible(false);
        }}
        width="800px"
        key="code-editor"
        footer={canEdit ? {
          enable: true,
          cancelText: "Close",
          okText: "Save",
          onOk: () => {
            let modalId: number = 7238212;
            let event = new KeyboardEvent("keydown", {
              key: "s",
              code: "KeyS",
              ctrlKey: true,
              detail: modalId,
            });
            document.dispatchEvent(event);
          },
        } : {
          enable: false
        }}
        style={{ top: '12px' }}
      >
        <CodeEditor
          data-test-id="validation_code_editor"
          value={pluginConfig.fun}
          json={currentState}
          lang="javascript"
          onSave={onEditorSave}
          height="300px"
          type="inline"
          saveMessage={{
            enable: true,
            message: "Validation saved succesfully",
          }}
          readOnly={!canEdit}
        />
      </Modal>
    </Fragment>
  );
}
